import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { userApi, agencyApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import DataTable from '../../components/DataTable.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Badge from '../../components/Badge.jsx';
import Modal from '../../components/Modal.jsx';
import { Field, Select, TextInput } from '../../components/Field.jsx';
import { formatDateTime } from '../../utils/format.js';
import { USER_ROLES as ROLES, AGENCY_ROLES } from '../../constants.js';


const EMPTY_FORM = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'TELEPRO',
  agencyId: '',
  phone: '',
};

const UsersPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const toMessage = useApiError();
  const { user: me } = useAuth();

  // Only offer the roles this account may actually hand out - the API enforces
  // the same list, so the dropdown can never promise something it will refuse.
  // Setting somebody else's password is USER_MANAGE, which an agency manager
  // does not hold - so the field is not drawn for them rather than drawn and
  // then refused by the API.
  const canSetPassword = me.role === 'ADMIN';

  const assignableRoles = me.manageableRoles ?? ROLES;

  const [filters, setFilters] = useState({ q: '', role: '' });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, refresh } = useApi(() => userApi.list(filters), [filters]);
  const { data: agencies } = useApi(() => agencyApi.list(), []);
  const agencyName = (id) => agencies?.items?.find((a) => a.id === id)?.name ?? '-';

  const submit = async () => {
    setBusy(true);
    try {
      if (form.id) {
        const { id, password, mustChangePassword, ...patch } = form;
        await userApi.update(id, patch);

        // A password is not part of the record: it is its own write, with its
        // own audit line, and it only happens if something was actually typed.
        if (canSetPassword && password.trim()) {
          await userApi.resetPassword(id, {
            newPassword: password.trim(),
            mustChangePassword: Boolean(mustChangePassword),
          });
        }
      } else {
        await userApi.create(form);
      }
      toast.success(form.id && form.password?.trim() ? t('users.passwordChanged') : t('common.save'));
      setForm(null);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (user) => {
    try {
      const result = await userApi.disable(user.id);
      toast.success(
        result.orphanCustomers
          ? `${t('users.disable')} - ${result.orphanCustomers} ${t('dashboard.customers').toLowerCase()}`
          : t('users.disable'),
      );
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const columns = [
    {
      key: 'name',
      header: t('users.title'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">
            {row.firstName} {row.lastName}
          </span>
          <span className="cell-muted">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: t('users.role'),
      render: (row) => <Badge value={row.role} label={t(`roles.${row.role}`)} />,
    },
    { key: 'agency', header: t('agencies.one'), render: (row) => agencyName(row.agencyId) },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <Badge value={row.status} label={row.status === 'ACTIVE' ? t('users.statusActive') : t('users.statusDisabled')} />
      ),
    },
    {
      key: 'lastLoginAt',
      header: t('users.lastLogin'),
      render: (row) => (row.lastLoginAt ? formatDateTime(row.lastLoginAt, i18n.resolvedLanguage) : t('users.never')),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (row) => (
        <div className="row-actions">
          {assignableRoles.includes(row.role) ? (
            <>
              <button
                type="button"
                className="btn btn--small"
                // Blank password on open: an empty box means "leave it alone".
                onClick={() => setForm({ ...row, password: '', mustChangePassword: true })}
              >
                {t('common.edit')}
              </button>
              {row.status === 'ACTIVE' ? (
                <button type="button" className="btn btn--small btn--ghost" onClick={() => disable(row)}>
                  {t('users.disable')}
                </button>
              ) : null}
            </>
          ) : (
            <span className="muted small">{t('users.notYours')}</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('users.title')}
        actions={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setForm({ ...EMPTY_FORM, role: assignableRoles[0] ?? 'TELEPRO' })}
          >
            {t('users.newUser')}
          </button>
        }
      />

      <Toolbar>
        <TextInput
          value={filters.q}
          onChange={(value) => setFilters({ ...filters, q: value })}
          placeholder={t('common.search')}
        />
        <Select
          value={filters.role}
          onChange={(value) => setFilters({ ...filters, role: value })}
          placeholder={t('common.all')}
          options={ROLES.map((role) => ({ value: role, label: t(`roles.${role}`) }))}
        />
        <span className="toolbar__count">
          {data?.total ?? 0} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable columns={columns} rows={data?.items} loading={loading} emptyTitle={t('common.noData')} />

      <Modal
        open={Boolean(form)}
        title={form?.id ? t('common.edit') : t('users.newUser')}
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={submit} disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {form ? (
          <div className="form-grid">
            <Field label={t('users.firstName')} htmlFor="u-first" required>
              <TextInput id="u-first" value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} />
            </Field>
            <Field label={t('users.lastName')} htmlFor="u-last" required>
              <TextInput id="u-last" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
            </Field>
            <Field label={t('auth.email')} htmlFor="u-email" required>
              <TextInput id="u-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            </Field>
            <Field label={t('customers.phone')} htmlFor="u-phone">
              <TextInput id="u-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </Field>
            <Field label={t('agencies.one')} htmlFor="u-agency" hint={t('agencies.userHint')}>
              <Select
                id="u-agency"
                value={form.agencyId ?? ''}
                onChange={(v) => setForm({ ...form, agencyId: v || null })}
                placeholder={t('common.none')}
                options={(agencies?.items ?? []).map((a) => ({ value: a.id, label: a.name }))}
                disabled={!AGENCY_ROLES.includes(form.role)}
              />
            </Field>
            <Field label={t('users.role')} htmlFor="u-role" required>
              <Select
                id="u-role"
                value={form.role}
                onChange={(v) => setForm({ ...form, role: v })}
                options={assignableRoles.map((role) => ({ value: role, label: t(`roles.${role}`) }))}
              />
            </Field>
            {!form.id ? (
              <Field label={t('auth.password')} htmlFor="u-password" required hint="min. 8">
                <TextInput
                  id="u-password"
                  type="password"
                  value={form.password}
                  onChange={(v) => setForm({ ...form, password: v })}
                />
              </Field>
            ) : (
              <Field label={t('common.status')} htmlFor="u-status">
                <Select
                  id="u-status"
                  value={form.status}
                  onChange={(v) => setForm({ ...form, status: v })}
                  options={[
                    { value: 'ACTIVE', label: t('users.statusActive') },
                    { value: 'DISABLED', label: t('users.statusDisabled') },
                  ]}
                />
              </Field>
            )}

            {/* Changing somebody else's password. Empty means untouched, so an
                edit to a phone number never silently resets a login. */}
            {form.id && canSetPassword ? (
              <div className="span-2">
                <Field
                  label={t('users.newPassword')}
                  htmlFor="u-newpassword"
                  hint={t('users.newPasswordHint')}
                >
                  <TextInput
                    id="u-newpassword"
                    type="password"
                    autoComplete="new-password"
                    value={form.password ?? ''}
                    onChange={(v) => setForm({ ...form, password: v })}
                  />
                </Field>
                <label className={form.mustChangePassword ? 'option option--checked' : 'option'}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.mustChangePassword)}
                    onChange={() => setForm({ ...form, mustChangePassword: !form.mustChangePassword })}
                  />
                  <span>{t('users.mustChangePassword')}</span>
                </label>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default UsersPage;
